/**
 * RssiSpark component.
 *
 * Phase 3 implementation: renders a rolling-window live sparkline using
 * synthetic RSSI values generated locally to simulate activity. A real
 * uPlot-backed sparkline with per-alert WebSocket subscription data ships
 * in Plan 03-07.
 *
 * Design spec reference: section 4 - "RSSI ──────╱╲╱╲╱╱ -48 dBm"
 *
 * @param currentRssi - Current RSSI value in dBm (null if unknown)
 */
import { useState, useEffect, useRef } from 'react';
import * as styles from './StoryCard.css';

interface Props {
  /** Current RSSI value in dBm for display. Null if not yet known. */
  currentRssi?: number | null;
}

/** Number of data points in the rolling window. */
const WINDOW_SIZE = 20;

/** Interval between synthetic updates in ms. */
const UPDATE_INTERVAL_MS = 500;

/** Spark SVG dimensions. */
const SPARK_WIDTH = 100;
const SPARK_HEIGHT = 24;

/** RSSI display range (dBm). */
const RSSI_MIN = -90;
const RSSI_MAX = -30;

/**
 * Maps a dBm RSSI value to a y pixel position within the spark SVG.
 * Higher (less negative) RSSI = lower y value (top of SVG).
 *
 * @param rssi - RSSI value in dBm
 * @returns y pixel coordinate
 */
function rssiToY(rssi: number): number {
  const clamped = Math.max(RSSI_MIN, Math.min(RSSI_MAX, rssi));
  const normalized = (clamped - RSSI_MIN) / (RSSI_MAX - RSSI_MIN);
  // Invert: higher RSSI = lower y
  return SPARK_HEIGHT - normalized * SPARK_HEIGHT;
}

/**
 * Converts an array of RSSI values to an SVG polyline points string.
 *
 * @param values - Array of RSSI dBm values
 * @returns SVG points attribute string
 */
function toPolylinePoints(values: number[]): string {
  if (values.length === 0) return '';
  const step = SPARK_WIDTH / Math.max(values.length - 1, 1);
  return values.map((v, i) => `${i * step},${rssiToY(v)}`).join(' ');
}

/**
 * RssiSpark: live RSSI sparkline with synthetic data (Phase 3 placeholder).
 *
 * Plan 03-07 replaces this with real uPlot instance subscribed to a
 * per-alert WS subchannel filtered by fingerprint.
 *
 * @param props - currentRssi for display
 */
export function RssiSpark({ currentRssi }: Props) {
  const [history, setHistory] = useState<number[]>(() => {
    // Seed with a plausible RSSI range to avoid a flat line on first render
    const seed: number[] = [];
    let v = currentRssi ?? -55;
    for (let i = 0; i < WINDOW_SIZE; i++) {
      v = v + (Math.random() * 6 - 3);
      v = Math.max(RSSI_MIN, Math.min(RSSI_MAX, v));
      seed.push(v);
    }
    return seed;
  });

  // Roll the window: add a new synthetic value every UPDATE_INTERVAL_MS.
  const lastRef = useRef<number>(currentRssi ?? -55);
  useEffect(() => {
    const interval = setInterval(() => {
      const next = Math.max(RSSI_MIN, Math.min(RSSI_MAX, lastRef.current + (Math.random() * 6 - 3)));
      lastRef.current = next;
      setHistory((prev) => {
        const updated = [...prev, next];
        return updated.length > WINDOW_SIZE ? updated.slice(updated.length - WINDOW_SIZE) : updated;
      });
    }, UPDATE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const displayRssi = currentRssi ?? Math.round(lastRef.current);
  const points = toPolylinePoints(history);

  return (
    <div className={styles.rssiRow}>
      <span className={styles.rssiLabel}>RSSI</span>
      <svg
        width={SPARK_WIDTH}
        height={SPARK_HEIGHT}
        viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
        aria-hidden="true"
        style={{ flex: 1 }}
      >
        <polyline
          points={points}
          fill="none"
          stroke="var(--colors-accent-gold)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.8}
        />
      </svg>
      <span
        style={{
          fontFamily: 'var(--fonts-mono)',
          fontSize: '11px',
          color: 'var(--colors-text-secondary)',
          flexShrink: 0,
        }}
      >
        {displayRssi} dBm
      </span>
    </div>
  );
}
